import { useEffect, useMemo } from 'react';
import { SectionEditDrawer } from '@/components/common/SectionEditDrawer';
import { useAuth } from '@/hooks/useAuth';
import { useCountries } from '@/hooks/useCountries';
import { useProfile } from '@/hooks/useProfile';
import { useUploadsInFlight } from '@/hooks/useUploadsInFlight';
import { useRetainedValue } from '@/hooks/useRetainedValue';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { joinTruthy } from '@/lib/utils/format.utils';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import { pickKeys } from '@/lib/utils/object.utils';
import {
  investorProfileSchema,
  toApiPayload,
  toFormValues,
  type InvestorProfileFormValues,
} from './schema';
import { useSaveInvestorProfile } from './useInvestorProfileMutations';
import { INVESTOR_FIELD_LABELS } from './investor-field-labels';
import { withNewProfilePrefill } from './investor-prefill';
import { getColleagueRowError } from './colleague-contact-errors';
import {
  INVESTOR_SECTION_ORDER,
  INVESTOR_SECTIONS,
  investorEditorConfig,
  type InvestorEditorTarget,
} from './investor-sections';
import { INVESTOR_SECTION_EDITORS, type InvestorSectionEditProps } from './InvestorSectionContent';

const S = UI_STRINGS.INVESTOR_PROFILE;

export interface InvestorSectionEditorProps {
  data: InvestorProfileData;
  entityId: string;
  /** The open section, `setup` for a new profile, or null when no panel is open. */
  section: InvestorEditorTarget | null;
  onClose: () => void;
}

/**
 * A section save sends only that section's fields (the endpoint is partial,
 * NFR-25); the setup save sends the whole profile, which a create needs.
 */
function sectionPayload(
  values: InvestorProfileFormValues,
  target: InvestorEditorTarget,
  fields: readonly string[],
  saved: InvestorProfileData,
): InvestorProfileData {
  const payload = toApiPayload(values, saved);
  return target === 'setup' ? payload : pickKeys(payload, fields);
}

/** One section's fields, or - for setup - every section under its own heading. */
function renderEditor(target: InvestorEditorTarget, props: InvestorSectionEditProps) {
  if (target !== 'setup') return INVESTOR_SECTION_EDITORS[target](props);
  return (
    <div className="flex flex-col gap-10">
      {INVESTOR_SECTION_ORDER.map((id) => (
        <section key={id} className="flex flex-col gap-4">
          <h3 className="text-h6 text-navy">{INVESTOR_SECTIONS[id].title}</h3>
          {INVESTOR_SECTION_EDITORS[id](props)}
        </section>
      ))}
    </div>
  );
}

/**
 * The investor profile's side-panel editor (Flow 1): one section's fields and
 * its save, or the one-panel setup of a new profile (FR-181).
 */
export function InvestorSectionEditor({
  data,
  entityId,
  section,
  onClose,
}: InvestorSectionEditorProps) {
  const uploads = useUploadsInFlight();
  const save = useSaveInvestorProfile(entityId, {
    onSaved: () => {
      onClose();
      toast.success(S.SAVE_SUCCESS);
    },
  });
  const shown = useRetainedValue<InvestorEditorTarget>(section, 'about');
  const config = investorEditorConfig(shown);
  // A profile that does not exist yet opens with the person's own name and login email
  // (FR-187, A-205); a saved value always wins, and an existing profile is untouched.
  const { user } = useAuth();
  const accountQuery = useProfile();
  const account = accountQuery.data;
  const { data: countries = [] } = useCountries();
  // The phone picker starts on the person's own country when it is known (FR-184).
  const phoneCountryIso2 = countries.find((country) => country.id === account?.countryId)?.iso2;
  const formData = useMemo(
    () =>
      withNewProfilePrefill(data, {
        contactName: joinTruthy([account?.firstName, account?.lastName], ' '),
        email: user?.email,
      }),
    [data, account?.firstName, account?.lastName, user?.email],
  );
  const defaultValues = useMemo(() => toFormValues(formData), [formData]);

  const { reset } = save;
  useEffect(() => {
    if (section) reset();
  }, [section, reset]);

  // The setup panel opens once the account name has loaded, so the pre-fill is never missed
  // by a drawer that reset before it arrived (the drawer resets only when it opens).
  const waitingForAccount = !data.id && accountQuery.isPending;

  return (
    <SectionEditDrawer<InvestorProfileFormValues>
      open={section !== null && !waitingForAccount}
      onClose={onClose}
      title={config.title}
      schema={investorProfileSchema}
      fields={config.fields}
      fieldLabels={INVESTOR_FIELD_LABELS}
      defaultValues={defaultValues}
      onSubmit={(values) => save.mutate(sectionPayload(values, shown, config.fields, data))}
      isSaving={save.isPending}
      // A rejected colleague row names its problem (MYN-202); shown inline in the open panel.
      errorMessage={
        save.isError
          ? (getColleagueRowError(save.error)?.message ??
            getApiErrorMessage(save.error, S.SAVE_ERROR))
          : undefined
      }
      saveDisabled={uploads.isUploading}
    >
      {(form) =>
        renderEditor(shown, {
          data: formData,
          form,
          trackUpload: uploads.track,
          defaultPhoneCountryIso2: phoneCountryIso2,
        })
      }
    </SectionEditDrawer>
  );
}
